/**
 * ListScaffold — the ONE shared list-page grammar (design-system §6 "one
 * DataTable"). Every resource list in the portal is a `PageHeader` + an optional
 * `SearchBox` + the server-driven `DataTable` + a row `…` action menu, with a
 * single `ConfirmDialog` for delete. This module folds that boilerplate into a
 * typed `ResourceListPage<T>` so a resource list is config, not copy-paste.
 *
 * It deliberately reuses the proven, dependency-light `DataTable` / `PageHeader`
 * / `SearchBox` / `useSearchFilter` from `../projects/_shared` (the canonical
 * server-driven table) rather than introducing a second table — there is only
 * one DataTable. `RowActions` standardises the View / Edit / Delete overflow so
 * lists read identically across all ~35 resources.
 */
import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
  type CrudSort,
} from "@refinedev/core";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";

/** A standardised row `…` menu: View · Edit · (extras) · Delete. */
export interface RowActionsProps {
  onShow?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Extra menu items rendered above the Delete separator (e.g. primary verbs). */
  extra?: React.ReactNode;
  /** Hide the destructive Delete item (append-only / read-only resources). */
  hideDelete?: boolean;
}

/**
 * The canonical list-row overflow menu. Stops propagation so a click inside the
 * menu never triggers the row's navigation.
 */
export function RowActions({
  onShow,
  onEdit,
  onDelete,
  extra,
  hideDelete,
}: RowActionsProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Icon name="more" size={16} aria-hidden />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onShow ? (
          <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        ) : null}
        {onEdit ? (
          <DropdownMenuItem onClick={onEdit}>
            <Icon name="edit" size={16} className="mr-2" aria-hidden /> Edit
          </DropdownMenuItem>
        ) : null}
        {extra}
        {!hideDelete && onDelete ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={onDelete}
            >
              <Icon name="delete" size={16} className="mr-2" aria-hidden /> Delete
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface ResourceListPageProps<T extends { id: string }> {
  /** Resource path (Refine `name`). */
  resource: string;
  /** Page title (e.g. "Companies"). */
  title: string;
  /** One-line description under the title. */
  description?: string;
  /** Label for the create button (e.g. "New Company"). Omit to hide create. */
  createLabel?: string;
  /** Columns — typically include an `actions` column built with `RowActions`. */
  columns: ColumnDef<T, unknown>[];
  /** Initial sort (defaults to `created_at desc`). */
  initialSort?: CrudSort[];
  /** Page size (default 25). */
  pageSize?: number;
  /** Placeholder for the search box. Omit to hide search. */
  searchPlaceholder?: string;
  /** Empty-state message. */
  emptyMessage?: string;
  /** Human label for the entity in the delete dialog (e.g. "company"). */
  deleteEntityLabel?: string;
  /**
   * Derive the delete-dialog name line from a record. Defaults to the entity
   * label (never the raw id — §6 bans ids on user-facing surfaces).
   */
  deleteName?: (record: T) => React.ReactNode;
  /** The pending-delete record getter is exposed via render-prop in columns. */
  pendingDelete: T | null;
  setPendingDelete: (record: T | null) => void;
}

/**
 * Generic server-driven list page. Wires `useTable`, the search filter, the
 * single `DataTable`, and the delete `ConfirmDialog`. Columns (including the
 * `actions` column) are supplied by the caller so each resource keeps its own
 * cell rendering while sharing every structural piece.
 */
export function ResourceListPage<T extends { id: string }>({
  resource,
  title,
  description,
  createLabel,
  columns,
  initialSort,
  pageSize = 25,
  searchPlaceholder,
  emptyMessage,
  deleteEntityLabel = "record",
  deleteName,
  pendingDelete,
  setPendingDelete,
}: ResourceListPageProps<T>) {
  const go = useGo();
  const { createUrl, showUrl } = useNavigation();
  const { mutate: deleteOne } = useDelete();

  const table = useTable<T, HttpError>({
    columns,
    refineCoreProps: {
      resource,
      pagination: { pageSize },
      sorters: {
        initial: initialSort ?? [{ field: "created_at", order: "desc" }],
      },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description={description}
        action={
          createLabel ? (
            <Button onClick={() => go({ to: createUrl(resource) })}>
              <Icon name="create" size={16} className="mr-2" aria-hidden />{" "}
              {createLabel}
            </Button>
          ) : undefined
        }
      />

      {searchPlaceholder ? (
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder={searchPlaceholder}
        />
      ) : null}

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage={emptyMessage}
        onRowClick={(row) => go({ to: showUrl(resource, row.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title={`Delete ${deleteEntityLabel}?`}
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium">
              {pendingDelete
                ? (deleteName?.(pendingDelete) ?? `this ${deleteEntityLabel}`)
                : ""}
            </span>
            . This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteOne({ resource, id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

export default ResourceListPage;
